import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';

import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type EmpowerLosFormValues = {
  clientId: string;
  clientSecret: string;
  realECClientId: string;
  realECClientSecret: string;
  realECSourceId: string;
};

interface EditEmpowerLosIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (_settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditEmpowerLosIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditEmpowerLosIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let clientId = '';
  let clientSecret = '';
  let realECClientId = '';
  let realECClientSecret = '';
  let realECSourceId = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    clientId = data.clientId || '';
    clientSecret = data.clientSecret || '';
    realECClientId = data.realECClientId || '';
    realECClientSecret = data.realECClientSecret || '';
    realECSourceId = data.realECSourceId || '';
  }

  const validationSchema = yup.object().shape({
    clientId: yup.string().required('Client ID is required'),
    clientSecret: yup.string().required('Client Secret is required'),
    realECClientId: yup.string().required('RealEC Client ID is required'),
    realECClientSecret: yup.string().required('RealEC Client Secret is required'),
    realECSourceId: yup.string().required('Source ID is required'),
  });

  const { control, handleSubmit, formState } = useForm<EmpowerLosFormValues>({
    defaultValues: {
      clientId: clientId,
      clientSecret: clientSecret,
      realECClientId: realECClientId,
      realECClientSecret: realECClientSecret,
      realECSourceId: realECSourceId,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Empower LOS Settings' : 'Empower LOS Settings'}</h3>
      <form
        autoComplete="off"
        onSubmit={handleSubmit((data) => {
          if (onSave) {
            let settings: IntegrationSettings;

            if (isEditMode && integrationSettings) {
              settings = {
                ...integrationSettings,
                vendor: IntegrationVendor.Empower,
                environment: environment,
                integrationData: JSON.stringify({
                  clientId: data.clientId,
                  clientSecret: data.clientSecret,
                  realECClientId: data.realECClientId,
                  realECClientSecret: data.realECClientSecret,
                  realECSourceId: data.realECSourceId,
                }),
              };
            } else {
              settings = {
                name: 'Empower LOS Integration',
                vendor: IntegrationVendor.Empower,
                status: IntegrationStatus.Active,
                environment: environment,
                integrationData: JSON.stringify({
                  clientId: data.clientId,
                  clientSecret: data.clientSecret,
                  realECClientId: data.realECClientId,
                  realECClientSecret: data.realECClientSecret,
                  realECSourceId: data.realECSourceId,
                }),
              };
            }

            onSave(settings);
          }
        })}
        noValidate
        className="pb-3 p-fluid p-input-filled"
      >
        <h4 className="mt-0 mb-3">Empower API Connection</h4>

        <div className="field mb-4">
          <FormInputText control={control} errors={errors} field="clientId" label="Client ID *" placeholder="Enter Client ID" />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="clientSecret"
            label="Client Secret *"
            placeholder="Enter Client Secret"
            feedback={false}
            toggleMask
          />
        </div>

        <h4 className="mt-2 mb-3">RealEC Exchange Connection</h4>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="realECClientId"
            label="RealEC Client ID *"
            placeholder="Enter RealEC Client ID"
          />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="realECClientSecret"
            label="RealEC Client Secret *"
            placeholder="Enter RealEC Client Secret"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="realECSourceId"
            label="Source ID *"
            placeholder="Enter Source ID"
          />
        </div>

        <div className="flex justify-content-end gap-2 mt-4" style={{ zIndex: 1000 }}>
          <Button
            type="button"
            label="Cancel"
            className="p-button p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
